<script>
import { isEqual, get } from 'lodash';
import { ref } from 'vue';
import { mapGetters, mapActions, mapMutations, mapState } from 'vuex';

import { GATEWAYS, CC_GATEWAYS, PAYMENT_METHODS, donationStatusIsAtLeast, widgetViews, sentryMessage, sleep } from '@/utils';

import store from '@/store';
import StripeElement from '@/components/StripeElement.vue';
import AddressAutocomplete from '@/components/AddressAutocomplete.vue';
import AppButton from '@/common/AppButton.vue';
import FormLayout from '@/layouts/FormLayout.vue';
import ZelleInstructions from '@/components/ZelleInstructions.vue';
import BankConnectIllustration from '@/components/BankConnectIllustration.vue';

import CardknoxIfield from '@/components/CardknoxIfield.vue';
import StripeService from '@/services/stripe';
import GrowService from '@/services/grow';
import { verifyCheckoutCaptcha } from '@/services/captcha';
import useZelleCheckout from '@/composables/useZelleCheckout';
import { getSentryScope } from '@/services/sentry';

const DONATION_FREQUENCY_LABELS = {
  single: 'One-time',
  weekly: 'Weekly',
  monthly: 'Monthly',
  yearly: 'Yearly'
};
const GROW_WALLET_OPEN_TIMEOUT_MS = 10000;
const GROW_WALLET_OPEN_ERROR = 'The payment window did not open. Please try again.';

const createGrowCheckoutError = (message, stage) => {
  const error = new Error(message);
  error.stage = stage;
  return error;
};

export default {
  name: 'PaymentForm',
  components: {
    StripeElement,
    CardknoxIfield,
    AddressAutocomplete,
    AppButton,
    FormLayout,
    BankConnectIllustration,
    ZelleInstructions
  },
  props: {
    active: Boolean
  },

  setup() {
    const form = ref(null);
    const donateWithCompany = ref(!!store.state.donation.donorDetails.companyName);
    return { form, donateWithCompany, zelle: useZelleCheckout({ store, form, companyRequired: donateWithCompany }) };
  },

  data() {
    return {
      cardElementReady: false,
      cardNumberComplete: false,
      cardExpiryComplete: false,
      cardCvcComplete: false,
      accountNumberComplete: false,
      paymentState: 'idle', // 'idle', 'processing', 'complete', 'failed',
      cardError: '',
      elementsObject: null,
      donationDirty: true, // TODO: Can be initialized to true
      isElementDirty: false,
      cardDescription: '',
      paymentRequest: null,
      requestPaymentMethodEvent: null,
      canMakePayment: null,
      tel: '',
      telFieldDirty: false,
      GATEWAYS,
      CC_GATEWAYS,
      PAYMENT_METHODS,
      selectedCharityBrand: 'ojc-fund',
      CHARITY_BRANDS: [
        { key: 'ojc-fund', icon: 'ojc-fund-logo', label: 'OJC Fund', scale: 0.8 },
        { key: 'donors-fund', icon: 'donors-fund-logo', label: 'The Donors Fund' },
        { key: 'matbia', icon: 'matbia-logo', label: 'Matbia' },
        { key: 'pledger', icon: 'pledger-logo', label: 'Pledger' }
      ]
    };
  },

  computed: {
    ...mapState('donation', { donation: (state) => state }),
    ...mapState('validation', { validation: (state) => state }),
    ...mapState(['orgId', 'isWidgetOpen']),
    ...mapGetters('donation', ['amountPlusFees', 'persistentDonationData', 'isUsingElementFields', 'isCharityCard']),
    ...mapGetters('validation', ['isDonationValid']),
    ...mapState('publicOrgSettings', {
      suggestedAddressCountry: (state) => state.suggestedAddressCountry,
      defaultSettings: (state) => state.settings,
      orgDetails: (state) => state.orgDetails
    }),
    ...mapGetters('campaign', ['shouldSkipAdditionalDetails']),
    ...mapState('campaign', ['id', 'settings']),

    isZelle() {
      return this.zelle.active;
    },
    companyName: {
      get() {
        return this.donation.donorDetails.companyName || '';
      },
      set(value) {
        this.$set(this.donation.donorDetails, 'companyName', value);
      }
    },
    paymentGateway() {
      return this.isZelle ? this.$store.state.publicOrgSettings.gateways.find((gateway) => gateway.provider === GATEWAYS.ZELLE) : this.donation.CCGateway;
    },
    isSubmitting() {
      return this.isZelle ? this.zelle.reporting : this.paymentState === 'processing';
    },
    canSubmitPayment() {
      return (this.isZelle ? this.zelle.detailsValid : this.isDonationValid) && !this.isSubmitting;
    },
    paymentError() {
      return this.isZelle ? this.zelle.error : this.paymentState === 'failed' ? this.cardError : '';
    },
    address: {
      get() {
        return this.donation.address;
      },
      set(value) {
        this.SET_ADDRESS(value);
      }
    },

    paymentMethodTitle() {
      const paymentMethodTitles = {
        [PAYMENT_METHODS.ZELLE]: 'Zelle',
        [PAYMENT_METHODS.CARD]: this.$t('payment.creditCard'),
        [PAYMENT_METHODS.ACH]: this.$t('payment.bankTransfer'),
        [PAYMENT_METHODS.BANK_WALLET]: 'Bit',
        [PAYMENT_METHODS.DAF]: 'Charity card'
      };
      return paymentMethodTitles[this.donation.paymentMethod];
    },
    charityCardRequiresCvv() {
      return this.selectedCharityBrand === 'donors-fund';
    },

    entirePaymentForm() {
      const { donation, cardNumberComplete, cardExpiryComplete, cardCvcComplete, accountNumberComplete } = this;
      let elements;
      if (this.isCharityCard) {
        elements = this.charityCardRequiresCvv ? [cardNumberComplete, cardExpiryComplete, cardCvcComplete] : [cardNumberComplete, cardExpiryComplete];
      } else if (this.donation.paymentMethod === PAYMENT_METHODS.ACH) {
        elements = [accountNumberComplete];
      } else {
        elements = [cardNumberComplete, cardCvcComplete, cardExpiryComplete];
      }
      return {
        donateWithCompany: this.donateWithCompany,
        phone: donation.donorDetails.phone,
        donorDetails: donation.donorDetails,
        billingAddress: donation.billingAddress,
        elements,
        donation,
        tel: this.tel
      };
    },
    donateButtonText() {
      if (this.isZelle) return this.zelle.reporting ? this.$t('common.wait') : "I've sent my payment";
      switch (this.paymentState) {
        case 'processing':
          return this.$t('common.wait');
        case 'complete':
          return this.$t('confirmation.thanks'); // is state used?
        case 'failed':
          return this.$t('payment.tryAgain');
      }
      if (this.donation.gateway === GATEWAYS.STRIPE && this.donation.paymentMethod === PAYMENT_METHODS.ACH) {
        return this.$t('payment.plaidConnect');
      }

      if (this.donation.gateway === GATEWAYS.GROW) {
        return this.settings?.customCtaText?.trim() || this.$t('common.continue');
      }

      const ctaTerm = this.settings?.ctaTerm?.toLowerCase();
      if (!ctaTerm) {
        return this.$t('payment.donate.donate');
      }
      return this.$t(`payment.donate.${ctaTerm}`);
    },
    donationAmountOrZero() {
      if (isNaN(this.amountPlusFees * 100)) {
        return 0;
      } else {
        return this.amountPlusFees;
      }
    },
    isTelFieldInvalid() {
      if (!this.telFieldDirty || !this.tel.number) {
        return false;
      }

      return this.telFieldDirty && !this.tel.valid;
    },
    telRequiredError() {
      if (!this.settings.optionalFields?.phoneNumber?.required) {
        return false;
      }
      return this.telFieldDirty && !this.tel.number;
    },
    backView() {
      return this.shouldSkipAdditionalDetails ? widgetViews.DONATION_FORM : widgetViews.ADDITIONAL_DETAILS;
    },
    isCanadaEntity() {
      return this.paymentGateway?.country === 'Canada';
    },
    isIsraeliGrowEntity() {
      return this.paymentGateway?.provider === CC_GATEWAYS.GROW && this.paymentGateway?.country === 'Israel';
    },
    collectAddress() {
      if (this.isCanadaEntity) return true;
      return this.settings.optionalFields?.address?.collect;
    },
    isAddressRequired() {
      if (this.isCanadaEntity) return true;
      return this.settings.optionalFields?.address?.required;
    },
    stripePaymentSucceeded() {
      return get(this.donation, 'stripeIntent.status') === 'succeeded';
    },
    achMandateText() {
      const businessName = this.orgDetails?.name || 'the organization';
      return `By continuing, you authorize ${businessName} to debit the bank account you connect for this donation. You may revoke this authorization by contacting ${businessName}.`;
    },
    recurringAchMandateText() {
      const businessName = this.orgDetails?.name || 'the organization';
      return `You also authorize ${businessName} to debit this bank account periodically according to the donation schedule shown above.`;
    }
  },

  watch: {
    tel(value) {
      this.SET_DONOR_DETAILS_PHONE(value.number);
      this.SET_IS_PHONE_VALID(value.valid);
    },
    // We must handle donation details being changed after the initial submission to the server
    persistentDonationData(newValue, oldValue) {
      if (!isEqual(newValue, oldValue)) {
        if (this.stripePaymentSucceeded) {
          return;
        }
        this.donationDirty = true;
        this.paymentState = 'idle';
      }
    },

    entirePaymentForm: {
      handler: 'validatePaymentDetails',
      immediate: true,
      deep: true
    }
  },

  methods: {
    ...mapActions('donation', [
      'createDonation',
      'confirmPaymentStripeElements',
      'confirmDonation',
      'confirmStripePaymentRequest',
      'confirmStripeAch',
      'donateWithCardknox',
      'donateWithCardknoxACH',
      'donateWithGrow'
    ]),
    ...mapMutations(['SET_WIDGET_VIEW', 'SET_SUCCESSFULLY_DONATED']),
    ...mapMutations('donation', [
      'SET_CARD_EXPIRY',
      'SET_DONOR_DETAILS',
      'SET_DONOR_DETAILS_EMAIL',
      'SET_DONOR_DETAILS_PHONE',
      'SET_BILLING_ADDRESS',
      'SET_PLAID_DATA',
      'SET_DONATION_ID'
    ]),
    ...mapMutations('validation', ['SET_IS_PAYMENT_DETAILS_VALID', 'SET_IS_PHONE_VALID']),

    reportGrowFailure(stage, error, level = 'error') {
      const errorMessage = error?.message || (typeof error === 'string' ? error : 'Unknown Grow checkout error');
      sentryMessage(`Grow checkout failed at ${stage}`, level, {
        name: 'Grow checkout',
        data: {
          stage,
          donationId: this.donation.donationId,
          message: errorMessage,
          status: error?.status
        }
      });
    },

    async validatePaymentDetails() {
      await this.$nextTick();
      if (!this.$refs.form) return;
      const formValid = !(await this.$refs.form.hasValidationErrors());
      const phoneValid = !this.settings.optionalFields?.phoneNumber?.required || this.validation.isPhoneValid;
      const elementsValid = !this.isUsingElementFields || Object.values(this.entirePaymentForm.elements).every(Boolean);
      this.SET_IS_PAYMENT_DETAILS_VALID(formValid && phoneValid && elementsValid);
    },

    async verifyHCaptcha() {
      try {
        await verifyCheckoutCaptcha(this.$store);
        return true;
      } catch (error) {
        sentryMessage(`hCaptcha verification failed: ${error.message}`, 'error');
        this.cardError = error.message;
        this.paymentState = 'failed';
        return false;
      }
    },

    onCompanyEnabledChange(enabled) {
      this.donateWithCompany = enabled;
      if (!enabled) this.companyName = '';
    },

    async donate() {
      if (this.isZelle) return this.zelle.reportSent();
      const state = this.paymentState;
      this.triggerFormErrors();
      // TODO validate with validation module
      if (state === 'processing' || !this.isDonationValid) {
        sentryMessage('Donor clicked disabled donate button', 'warning', {
          name: 'Store',
          data: { validation: store.state.validation, donation: store.state.donation }
        });
        return;
      }

      this.paymentState = 'processing';

      const captchaVerified = await this.verifyHCaptcha();
      if (!captchaVerified) {
        return;
      }

      // TODO: Create *single* entry and exit point for all donation methods to avoid a lot of redundant code
      switch (this.donation.paymentMethod) {
        case PAYMENT_METHODS.CARD:
        case PAYMENT_METHODS.BANK_WALLET:
        case PAYMENT_METHODS.DAF:
          return this.donateWithElements();
        case PAYMENT_METHODS.ACH:
          return this.donateWithACH();
      }
    },

    async donateWithElements() {
      switch (this.donation.CCGateway.provider) {
        case GATEWAYS.CARDKNOX:
          try {
            await this.donateWithCardknox();
            return this.paymentSucceeded();
          } catch (err) {
            this.paymentState = 'failed';
            this.cardError = err.message;
          }
          break;

        case GATEWAYS.STRIPE:
          try {
            // Step 1 - Create the payment intent
            if (this.donationDirty && !this.stripePaymentSucceeded) {
              await this.createDonation();
              this.donationDirty = false;
            }
            // Step 2 - Confirm the payment intent
            if (!this.stripePaymentSucceeded) {
              await this.confirmPaymentStripeElements(this.$refs.cardNumber.element);
            }
            // Step 3 - Confirm the donation on our server
            const { status, error } = await this.confirmDonation();
            if (!error && donationStatusIsAtLeast(status, 'subscriptionScheduled')) {
              this.paymentSucceeded();
            } else {
              this.paymentState = 'failed';
              // todo: case payment succeeded but donation failed on our server
              this.cardError = `Problem: status: ${status}, error: ${error}`;
            }
          } catch (err) {
            this.paymentState = 'failed';
            this.cardError = err.message;
            sentryMessage(`Donation with elements failed: ${err.message}`, 'info');
          }
          break;

        case GATEWAYS.GROW: {
          let walletOpenTimeoutId;
          try {
            const grow = await GrowService.getGrowObject();
            const token = await this.donateWithGrow();
            let walletOpened = false;
            let resolveWalletOpen;
            let rejectWalletOpen;
            const walletOpenPromise = new Promise((resolve, reject) => {
              resolveWalletOpen = resolve;
              rejectWalletOpen = reject;
              walletOpenTimeoutId = setTimeout(() => {
                reject(createGrowCheckoutError(GROW_WALLET_OPEN_ERROR, 'wallet-open'));
              }, GROW_WALLET_OPEN_TIMEOUT_MS);
            });
            const handleProviderFailure = (stage, response, fallbackMessage, level = 'error') => {
              const error = createGrowCheckoutError(response?.message || fallbackMessage, stage);
              error.status = response?.status;
              clearTimeout(walletOpenTimeoutId);
              if (!walletOpened) {
                rejectWalletOpen(error);
                return;
              }
              this.reportGrowFailure(stage, error, level);
              this.paymentState = 'failed';
              this.cardError = error.message;
            };

            // Set up event handlers before rendering payment options
            GrowService.setEventCallbacks({
              onSuccess: () => {
                clearTimeout(walletOpenTimeoutId);
                resolveWalletOpen();
                this.paymentSucceeded();
              },
              onFailure: (response) => {
                handleProviderFailure('provider-failure', response, 'Payment failed', 'info');
              },
              onError: (response) => {
                handleProviderFailure('provider-error', response, 'An error occurred');
              },
              onTimeout: (response) => {
                handleProviderFailure('provider-timeout', response, 'Payment request timed out', 'warning');
              },
              onWalletChange: (state) => {
                const walletState = String(state).toLowerCase();
                getSentryScope().addBreadcrumb({
                  category: 'grow',
                  message: `Grow wallet ${walletState}`,
                  data: { donationId: this.donation.donationId }
                });
                if (walletState === 'open') {
                  walletOpened = true;
                  clearTimeout(walletOpenTimeoutId);
                  resolveWalletOpen();
                } else if (walletState === 'close') {
                  clearTimeout(walletOpenTimeoutId);
                  if (walletOpened) {
                    this.paymentState = 'idle';
                  } else {
                    rejectWalletOpen(createGrowCheckoutError(GROW_WALLET_OPEN_ERROR, 'wallet-closed-before-open'));
                  }
                }
              },
              onPaymentStart: (response) => {
                getSentryScope().addBreadcrumb({
                  category: 'grow',
                  message: 'Grow payment started',
                  data: {
                    donationId: this.donation.donationId,
                    paymentType: response?.paymentType,
                    paymentName: response?.paymentName
                  }
                });
              },
              onPaymentCancel: () => {
                clearTimeout(walletOpenTimeoutId);
                if (walletOpened) {
                  this.paymentState = 'idle';
                } else {
                  rejectWalletOpen(createGrowCheckoutError(GROW_WALLET_OPEN_ERROR, 'payment-cancelled-before-open'));
                }
              }
            });

            grow.init();
            grow.renderPaymentOptions(token);
            await walletOpenPromise;
          } catch (err) {
            clearTimeout(walletOpenTimeoutId);
            const stage = err.stage || 'unknown';
            this.reportGrowFailure(stage, err);
            this.paymentState = 'failed';
            this.cardError = err.message || 'The Grow payment form could not be opened. Please try again.';
          }
          break;
        }

        default:
          throw new Error('Something went wrong');
      }
    },

    // ACH
    async donateWithACH() {
      switch (this.donation.CCGateway.provider) {
        case GATEWAYS.CARDKNOX:
          try {
            await this.donateWithCardknoxACH();
            return this.paymentSucceeded();
          } catch (err) {
            this.paymentState = 'failed';
            this.cardError = err.message;
          }
          break;

        case GATEWAYS.STRIPE:
          try {
            if (this.donationDirty) {
              await this.createDonation();
              this.donationDirty = false;
            }
            if (this.donation.stripeIntent?.clientSecret) {
              await this.confirmStripeAch();
            } else {
              const { publicToken, metadata } = await this.confirmPaymentPlaid();
              this.SET_PLAID_DATA({ publicToken, metadata });
            }

            const { status, error } = await this.confirmDonation();
            const minimumSuccessStatus = this.donation.frequency === 'single' ? 'pending' : 'subscriptionScheduled';
            if (!error && donationStatusIsAtLeast(status, minimumSuccessStatus)) {
              return this.paymentSucceeded();
            } else {
              this.paymentState = 'failed';
              this.cardError = `Problem: status: ${status}, error: ${error}`;
            }
          } catch (err) {
            // if (err !== 'null') {
            //   // user cancelled
            //   this.paymentState = 'idle';
            // } else {
            //   this.paymentState = 'failed';
            //   this.cardError = err.message;
            // }
            this.paymentState = 'failed';
            this.cardError = err.message;
          }
      }
    },

    async confirmPaymentPlaid() {
      return new Promise((resolve, reject) => {
        // const iframe = document.querySelector('iframe[name="double-checkout"]');
        // const Plaid = iframe.contentWindow.Plaid;
        if (!Plaid) {
          reject(new Error('Plaid is not loaded!'));
        }
        const config = {
          token: this.donation.plaidLinkToken.link_token,
          onSuccess: async (publicToken, metadata) => {
            resolve({ publicToken, metadata });
          },
          onExit: async (err) => {
            console.error(err);
            reject(new Error(err)); // TODO: refresh token. If null - user cancelled
          }
        };

        /* globals Plaid */
        const linkHandler = Plaid.create(config);
        linkHandler.open();
      });
    },

    // UI methods
    async createElements() {
      if (this.paymentGateway?.provider !== CC_GATEWAYS.STRIPE) {
        return;
      }
      const stripe = await StripeService.getStripeObject();
      this.elementsObject = stripe.elements({
        fonts: [
          {
            cssSrc: 'https://fonts.googleapis.com/css2?family=Nunito&display=swap'
          }
        ],
        locale: 'auto'
      });
    },

    triggerFormErrors() {
      // Trigger fake submission
      this.$refs.hiddenSubmitButton.click();
      this.isElementDirty = true;
      this.telFieldDirty = true;
      // Notify other components
      this.$emit('donation-attempt');
      // Scroll to first error

      this.$nextTick(() => {
        const customErrorMessage = document.querySelector('.section-has-error');
        const firstFieldElementWithError = document.querySelector('[data-is-showing-errors=true]');
        const elementToScrollTo = customErrorMessage || firstFieldElementWithError;
        if (elementToScrollTo) {
          elementToScrollTo.scrollIntoView({ behavior: 'smooth' });
        }
      });
    },

    // Help methods
    paymentSucceeded() {
      this.paymentState = 'complete';
      this.SET_WIDGET_VIEW(widgetViews.CONFIRMATION_DONATION);
      this.SET_DONATION_ID(null);
      this.$doubleEvent('Double.donationCompleted');
      this.$trackEvent({
        action: 'success',
        label: DONATION_FREQUENCY_LABELS[this.donation.frequency] + ' donation',
        value: this.donationAmount
      });
      this.SET_SUCCESSFULLY_DONATED(true);
    },
    async setEmail(email) {
      if (await this.$refs.email?.hasValidationErrors()) {
        return;
      }
      this.SET_DONOR_DETAILS_EMAIL(email);
    }
  },

  async mounted() {
    // TEMP: emulate donation completed EVENT for Elazraki
    if (this.orgId === '6296ca90-32ba-11ef-b836-0d5f75516076' && this.donation.paymentMethod === PAYMENT_METHODS.ACH) {
      console.log('6296ca90-32ba-11ef-b836-0d5f75516076');
      this.$doubleEvent('Double.donationCompleted');
    }
    this.createElements();

    if (this.donation.gateway === GATEWAYS.GROW) {
      try {
        const grow = await GrowService.getGrowObject();
        grow.init();
        await sleep(1000);
      } catch (error) {
        this.reportGrowFailure(error.stage || 'sdk-preload', error, 'warning');
      }
    }

    // Captcha is now initialized when user clicks donate in Form.vue
  }
};
</script>

<template>
  <form-layout :title="paymentMethodTitle" :back-view="backView">
    <div ref="paymentDetails">
      <div>
        <formulate-form ref="form">
          <div class="my-8">
            <h4 class="body-text-extra-bold mb-2">Personal information</h4>
            <div class="flex flex-col gap-2">
              <formulate-input
                @input="setEmail"
                :value="donation.donorDetails.email"
                type="email"
                validation="^required|email"
                validation-name="Email"
                ref="email"
                :placeholder="$t('payment.email')"
                name="email"
                class="w-full"
              />
              <div class="flex gap-2">
                <formulate-input
                  v-model="donation.donorDetails.firstName"
                  type="text"
                  validation="required"
                  validation-name="First name"
                  :placeholder="$t('common.firstName')"
                  name="firstName"
                  autocomplete="given-name"
                  class="flex-1"
                />
                <formulate-input
                  v-model="donation.donorDetails.lastName"
                  type="text"
                  validation="required"
                  validation-name="Last name"
                  :placeholder="$t('common.lastName')"
                  name="lastName"
                  autocomplete="family-name"
                  class="flex-1"
                />
              </div>
              <!-- Loaded globally in widget-iframe.vue  -->
              <template v-if="isZelle && !settings.hideCompanyDonation">
                <app-checkbox :value="donateWithCompany" @input="onCompanyEnabledChange">{{ $t('payment.behalfCompany') }}</app-checkbox>
                <formulate-input
                  v-if="donateWithCompany"
                  v-model="companyName"
                  type="text"
                  validation="required"
                  validation-name="Company name"
                  :label="$t('payment.companyName')"
                  :placeholder="$t('payment.companyName')"
                  name="companyName"
                  autocomplete="organization"
                />
              </template>
              <div v-if="settings?.optionalFields?.phoneNumber?.collect || paymentGateway?.provider === CC_GATEWAYS.GROW">
                <!--
                  Tried to integrate it with Formulate, but it seems impossible since the tel lib doesn't expose
                  a slot for the input, so we can't bind to it the formulate properties
                -->
                <vue-tel-input
                  :only-countries="paymentGateway?.provider === CC_GATEWAYS.GROW ? ['IL'] : []"
                  :default-country="paymentGateway?.provider === CC_GATEWAYS.GROW ? 'IL' : suggestedAddressCountry || 'US'"
                  :auto-default-country="false"
                  @blur="telFieldDirty = true"
                  @validate="tel = $event"
                  :class="{ invalid: isTelFieldInvalid || telRequiredError }"
                />
                <p v-if="telRequiredError" class="mt-1 px-1 text-red text-xxs">Phone number is required.</p>
                <p v-else-if="isTelFieldInvalid" class="mt-1 px-1 text-red text-xxs">"{{ tel.number }}" is not a valid phone number.</p>
              </div>
              <formulate-input
                v-if="isIsraeliGrowEntity"
                v-model="donation.donorDetails.invoiceLicenseNumber"
                type="text"
                validation="required"
                validation-name="Israeli ID number"
                placeholder="Israeli ID number"
                name="invoiceLicenseNumber"
                inputmode="numeric"
                class="w-full"
              />

            </div>
          </div>

          <div v-if="donation.paymentMethod === PAYMENT_METHODS.CARD && isUsingElementFields" class="my-8">
            <h4 class="body-text-extra-bold mb-2 flex items-center gap-0.5">
              <span>Card information</span>
              <app-tooltip>
                <app-icon name="heart-lock" height="18" width="18" type="svg" />
                <template #content>
                  <p>Our checkout is secured by industry-standard 256 bit SSL encryption</p>
                </template>
              </app-tooltip>
            </h4>

            <div class="flex flex-col gap-2">
              <stripe-element
                v-if="donation.CCGateway.provider === CC_GATEWAYS.STRIPE && elementsObject"
                @state-changed="cardNumberComplete = $event === 'complete'"
                @ready="cardElementReady = $event"
                :elements-object="elementsObject"
                element-type="cardNumber"
                :make-dirty="isElementDirty"
                ref="cardNumber"
              />
              <cardknox-ifield
                v-else-if="donation.CCGateway.provider === CC_GATEWAYS.CARDKNOX"
                @state-changed="cardNumberComplete = $event === 'complete'"
                @ready="cardElementReady = $event"
                ifield-id="card-number"
                ref="cardknox-card-number"
                :label="this.$t('payment.cardNumber')"
                :make-dirty="isElementDirty"
              />
              <div class="flex gap-2">
                <template v-if="donation.CCGateway.provider === CC_GATEWAYS.STRIPE && elementsObject">
                  <stripe-element
                    class="flex-1"
                    @state-changed="cardExpiryComplete = $event === 'complete'"
                    :elements-object="elementsObject"
                    element-type="cardExpiry"
                    :make-dirty="isElementDirty"
                  />
                  <stripe-element
                    class="flex-1"
                    @state-changed="cardCvcComplete = $event === 'complete'"
                    :elements-object="elementsObject"
                    element-type="cardCvc"
                    :make-dirty="isElementDirty"
                  />
                </template>
                <template v-else-if="donation.CCGateway.provider === CC_GATEWAYS.CARDKNOX">
                  <div class="flex-1">
                    <content-loader v-if="!cardElementReady" />
                    <card-exp-field
                      v-else
                      @validate="
                        SET_CARD_EXPIRY($event.isValid ? $event.value : null);
                        $event.isValid ? (cardExpiryComplete = true) : (cardExpiryComplete = false);
                      "
                      :make-dirty="isElementDirty"
                    />
                  </div>

                  <cardknox-ifield
                    @state-changed="cardCvcComplete = $event === 'complete'"
                    ifield-id="cvv"
                    ref="cardknox-cvv"
                    label="CVV"
                    class="flex-1"
                    :make-dirty="isElementDirty"
                  />
                </template>
                <div class="flex-1">
                  <content-loader v-if="!cardElementReady" />
                  <formulate-input
                    v-else
                    v-model="donation.donorDetails.zip"
                    type="text"
                    validation="required"
                    validation-name="ZIP"
                    placeholder="ZIP"
                    name="zip"
                    maxlength="9"
                  />
                </div>
              </div>
            </div>
          </div>

          <div v-if="isCharityCard" class="my-8">
            <h4 class="body-text-extra-bold mb-2 flex items-center gap-0.5">
              <span>Card information</span>
              <app-tooltip>
                <app-icon name="heart-lock" height="18" width="18" type="svg" />
                <template #content>
                  <p>Our checkout is secured by industry-standard 256 bit SSL encryption</p>
                </template>
              </app-tooltip>
            </h4>

            <div class="flex flex-col gap-2">
              <div class="charity-brand-selector flex">
                <button
                  type="button"
                  v-for="brand in CHARITY_BRANDS"
                  :key="brand.key"
                  @click="selectedCharityBrand = brand.key"
                  class="charity-brand-tab flex-1 flex items-center justify-center cursor-pointer"
                  :class="{ 'charity-brand-tab--active': selectedCharityBrand === brand.key }"
                >
                  <app-icon type="svg" :name="brand.icon" class="w-full" :style="{ transform: `scale(${brand.scale})` }" :inner-style="{ width: '100%' }" />
                </button>
              </div>

              <cardknox-ifield
                @state-changed="cardNumberComplete = $event === 'complete'"
                @ready="cardElementReady = $event"
                ifield-id="card-number"
                ref="cardknox-card-number"
                :label="this.$t('payment.cardNumber')"
                :make-dirty="isElementDirty"
              />
              <div class="flex gap-2">
                <div class="flex-1">
                  <content-loader v-if="!cardElementReady" />
                  <card-exp-field
                    v-else
                    @validate="
                      SET_CARD_EXPIRY($event.isValid ? $event.value : null);
                      $event.isValid ? (cardExpiryComplete = true) : (cardExpiryComplete = false);
                    "
                    :make-dirty="isElementDirty"
                  />
                </div>
                <cardknox-ifield
                  v-if="charityCardRequiresCvv"
                  @state-changed="cardCvcComplete = $event === 'complete'"
                  ifield-id="cvv"
                  ref="cardknox-cvv"
                  label="CVV"
                  class="flex-1"
                  :make-dirty="isElementDirty"
                />
              </div>
            </div>
          </div>

          <div v-if="collectAddress" class="my-8">
            <h4 class="body-text-extra-bold mb-2">Billing information</h4>
            <div class="flex flex-col gap-2">
              <address-autocomplete @update="SET_BILLING_ADDRESS($event)" :address-required="isAddressRequired" />
            </div>
          </div>

          <div v-if="donation.gateway === GATEWAYS.STRIPE && donation.paymentMethod === PAYMENT_METHODS.ACH" class="p-7 plaid-fwd-msg rounded-2xl">
            <bank-connect-illustration />
            <h3 class="bold text-center">{{ $t('payment.plaidTitle') }}</h3>
            <div class="mt-1 body-text bold text-black-01 text-center">{{ $t('payment.plaidHelp') }}</div>
            <div class="mt-4 body-text text-black-01 text-center">{{ achMandateText }}</div>
            <div v-if="donation.frequency !== 'single'" class="mt-2 body-text text-black-01 text-center">{{ recurringAchMandateText }}</div>
          </div>

          <div v-if="donation.gateway === GATEWAYS.CARDKNOX && donation.paymentMethod === PAYMENT_METHODS.ACH">
            <h4 class="body-text-extra-bold mb-2">Account information</h4>
            <div class="flex flex-col gap-2">
              <!-- Account name -->
              <formulate-input
                v-model="donation.accountName"
                type="text"
                validation="required"
                validation-name="Account name"
                placeholder="Account name"
                name="accountName"
                class="w-full"
              />
              <div class="flex gap-2">
                <cardknox-ifield
                  @state-changed="accountNumberComplete = $event === 'complete'"
                  ifield-id="ach"
                  ref="cardknox-account-number"
                  label="Account number"
                  :make-dirty="isElementDirty"
                />

                <formulate-input
                  v-model="donation.routingNumber"
                  type="text"
                  validation="required"
                  validation-name="Routing number"
                  placeholder="Routing number"
                  name="routingNumber"
                  class="w-full"
                />
              </div>
            </div>
          </div>
          <zelle-instructions
            v-if="isZelle"
            :instructions="donation.zelleInstructions"
            :gateway="paymentGateway"
            :donation-amount="donation.amount"
            :saving="zelle.saving"
          />
          <!-- a hidden button to trigger the display of the error with a fake  submission -->
          <button ref="hiddenSubmitButton" class="hidden-submit-button"></button>
        </formulate-form>
      </div>
    </div>
    <template #footer>
      <div v-if="paymentError" class="custom-error-message">Error: {{ paymentError }}</div>
      <app-button v-if="isZelle && zelle.error && !zelle.ready" variant="outline" type="button" @click="zelle.save">Retry saving details</app-button>
      <app-button @click="donate" id="donate-button" class="primary w-full flex justify-center items-center" :disabled="!canSubmitPayment" :native-disabled="isZelle">
        <h3 v-if="isSubmitting" class="flex items-center gap-1">
          <app-loader />
          <span> {{ $t('common.wait') }}</span>
        </h3>
        <h3 v-else class="flex gap-1">
          <span>{{ donateButtonText }}</span>
          <app-price
            v-if="
              (donation.paymentMethod === PAYMENT_METHODS.CARD || isCharityCard) &&
              paymentState !== 'failed' &&
              donation.gateway !== GATEWAYS.GROW &&
              settings?.ctaTerm?.toLowerCase() !== 'purchase'
            "
            :amount="donationAmountOrZero"
            :currency="donation.currency"
            :frequency="donation.frequency"
            show-frequency
          />
        </h3>
      </app-button>
    </template>
  </form-layout>
</template>
